import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { pick } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateBuyOrder } from 'services/buy-process';
import { useBuyProcessForm } from '../..';
import {
  onGoBackInForm,
  onGoForwardInForm,
  useSaveBuyOrderFormState,
  useSelectPeople,
} from '../../use-buy-process-form';
import { saveEditProxies } from '../../../admin/buy-process/buy-order-edit/buy-order-edit-redux';
import { useIsAdminOnBuyOrderEditForm } from '../../../../utils/buy-order/use-is-admin-on-buy-order-edit-form';
import { selectBuyPowerOfAttorneyPersonFactory } from '../../../../services/buy-process/selectors';
import { ProxiesFormState } from './proxies-types';
import { proxiesValidationSchema } from './proxies-utils';

export const useProxies = () => {
  const { setRequestedStep, id } = useBuyProcessForm();
  const initialState = useSelector(selectBuyPowerOfAttorneyPersonFactory(id));

  const formMethods = useForm<ProxiesFormState>({
    mode: 'onChange',
    defaultValues: { ...initialState },
    resolver: yupResolver(proxiesValidationSchema),
  });

  useSaveBuyOrderFormState(
    saveEditProxies,
    formMethods.getValues(),
    formMethods.trigger,
  );

  const people = useSelectPeople(id).map((person) =>
    pick(person, ['firstName', 'lastName', 'socialSecurityNumber']));

  const { register } = formMethods;
  useEffect(() => {
    register('firstName');
  }, [register]);

  const setFirstName = (name?: string) => {
    formMethods.setValue('firstName', name?.trim() ?? '', {
      shouldValidate: true,
    });
  };

  const setLastName = (lastName?: string) => {
    formMethods.setValue('lastName', lastName?.trim() ?? '', {
      shouldValidate: true,
    });
  };

  const setSsn = (ssn?: string) => {
    formMethods.setValue('socialSecurityNumber', ssn?.trim() ?? '', {
      shouldValidate: true,
    });
  };

  const dispatch = useDispatch();
  const isAdmin = useIsAdminOnBuyOrderEditForm();
  const [updateBuyOrder, updateBuyOrderMeta] = useUpdateBuyOrder(id);

  return {
    people,
    register,
    setFirstName,
    setLastName,
    setSsn,
    formMethods,
    isUpdating: updateBuyOrderMeta.isLoading,
    onSubmit: formMethods.handleSubmit((data) => {
      dispatch(saveEditProxies({ ...data, id }));
      updateBuyOrder();

      if (!isAdmin) {
        setRequestedStep((s) => s + 1);
      }
    }),
    onForward: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoForwardInForm(setRequestedStep, e),
    onBack: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoBackInForm(setRequestedStep, formMethods.trigger, e),
  };
};
